import { ArrowDown, ArrowUp, ChevronUp, Info, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  BasicsState,
  PeriodOverride,
  PricingState,
} from "@/lib/plan-editor";

/**
 * Phase 27 Plan 04 — Plan Editor Pricing section (3-column FIXED rewrite).
 *
 * The Phase 26 Mode toggle column is REMOVED. The Phase 26 Discount %/Price
 * twin-column shape is REPLACED with 3 fixed columns in this order:
 *
 *   Cycle (value + unit) | Seats (NEW) |
 *   Baseline (₹) (NEW read-only computed) |
 *   Discount % (input 0-100) |
 *   Discounted (₹) (NEW read-only computed) |
 *   Deposit | Amount | Validity | Actions
 *
 * Computed cells:
 *   Baseline   = monthlyPrice × seats × periodValue       (raw value, see RESEARCH §periodMonths)
 *   Discounted = Baseline × (1 - discount/100)
 *
 * Baseline row (pinned, read-only): seats=1, discount=0, Baseline === Discounted.
 *
 * Legacy hydration (Phase 26 priceMode='price' rows):
 *   - PeriodOverride.isLegacyAbsolute flips true at hydration time.
 *   - Discounted cell renders ₹{price} + a "Legacy absolute" Badge.
 *   - Seats, Baseline, Discount cells render '—' (non-editable).
 *   - Operator can archive the row but not edit it; recreate as discount-mode
 *     to migrate forward.
 *
 * savePricing pipeline (plan-editor.tsx Task 2 portion):
 *   - Always writes discount_pct on upsert (never `price`); discount_pct may be 0
 *     for baseline-like rows.
 *   - DELETE uses useDeleteInventoryPlanPeriodById(planId, periodRowId) so the
 *     archive call targets the precise row by id (no cycle-string ambiguity
 *     when multiple seats rows share the same period).
 *
 * Cadence-aware Add-Row defaults preserved from Phase 26:
 *   Recurring         → Month, seats=1
 *   Non-Recurring     → Day,   seats=1
 *   Conference_Room   → Hour,  seats=1
 *
 * Covers: PRICE27-01, PRICE27-02, PRICE27-03, PRICE27-04, SEATS27 UI surface.
 *         ARCHIVE26-03 + VALIDITY26-01/02 (Phase 26 regressions preserved).
 */

export interface PricingSectionProps {
  value: PricingState;
  onChange: (next: PricingState) => void;
  /** Read-only baseline source (monthlyPrice + validityDays + cadence + resourceType). */
  basics: BasicsState;
  /** Plan 25-04 — Save & Continue button click handler. */
  onSave: () => void | Promise<void>;
  /** Plan 25-04 — true while any section save is in flight. */
  saving: boolean;
  /**
   * Optional focus-jump affordance: clicking the baseline row's "(from Basics)"
   * chevron asks the parent to re-open the Basics accordion.
   */
  onFocusBasics?: () => void;
  /**
   * 2026-06-09 — patch a subset of BasicsState from the Pricing baseline row.
   * Lets operator edit monthlyPrice + validityDays inline without jumping to
   * Basics. Page wires this to `updateBasics(prev => ({...prev, ...patch}))`.
   */
  onBasicsChange?: (patch: Partial<BasicsState>) => void;
  /** 2026-06-09 — locks all inputs when true */
  disabled?: boolean;
  /** 2026-06-09 — Edit button handler */
  onEdit?: () => void;
  /**
   * Versioned-pricing lock: when true (editing an EXISTING plan) the baseline ₹
   * is read-only and the operator changes it only via the "Change Price" flow
   * (monotonic increase + version history). Prevents an unguarded lower/rewrite
   * through the plan editor. Left false on plan creation (initial price is set
   * inline). See change-price-dialog.tsx.
   */
  priceLocked?: boolean;
  /** Opens the Change Price dialog. Rendered next to the baseline ₹ when priceLocked. */
  onChangePrice?: () => void;
}

/**
 * Cadence-aware unit default for a freshly-added operator row.
 * Phase 27: priceMode is no longer a UI concept — new rows always operate in
 * discount mode. We keep the cadence → unit mapping intact.
 */
function addRowDefaultUnit(basics: BasicsState): PeriodOverride["periodUnit"] {
  if (basics.resourceType === "Conference_Room") return "Hour";
  if (basics.cadence === "Recurring") return "Month";
  return "Day";
}

function makeEmptyRow(basics: BasicsState): PeriodOverride {
  return {
    periodValue: "",
    periodUnit: addRowDefaultUnit(basics),
    seats: 1,
    // priceMode/price kept on the type for hydration compat; new rows leave
    // priceMode UNDEFINED so they pass through the discount-mode path.
    discountPct: "",
    price: "",
    securityDepositRequired: false,
    securityDepositAmount: "",
    validityDays: "",
    status: "Active",
  };
}

/**
 * Section validity (Phase 25/26 contract preserved + Phase 27 seats invariant):
 *   - Plan-level Deposit toggle ON ⇒ amount required.
 *   - Active operator rows: periodValue > 0 AND seats >= 1.
 *   - Per-row deposit toggle ON ⇒ amount required.
 *   - Legacy isLegacyAbsolute rows are not validated (read-only).
 */
function computeIsSectionValid(p: PricingState): boolean {
  if (p.securityDepositRequired && !p.securityDepositAmount) return false;
  for (const r of p.periodOverrides) {
    if (r.status !== "Active") continue;
    if (r.isLegacyAbsolute) continue;
    if (r.periodValue === "" || Number(r.periodValue) <= 0) return false;
    if (!r.seats || r.seats < 1) return false;
    if (r.securityDepositRequired && !r.securityDepositAmount) return false;
  }
  return true;
}

/** Inline INR formatter (en-IN locale, no decimals). */
function formatRupee(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount)}`;
}

export function PricingSection({
  value,
  onChange,
  basics,
  onSave,
  saving,
  onFocusBasics,
  onBasicsChange,
  disabled = false,
  onEdit,
  priceLocked = false,
  onChangePrice,
}: PricingSectionProps) {
  const isRecurring = basics.cadence === "Recurring";
  const monthlyPriceNum = Number(basics.monthlyPrice) || 0;

  function patch(next: Partial<PricingState>) {
    onChange({ ...value, ...next });
  }

  function patchRow(
    rowIdx: number,
    next: Partial<PeriodOverride>,
  ): void {
    onChange({
      ...value,
      periodOverrides: value.periodOverrides.map((r, i) =>
        i === rowIdx ? { ...r, ...next } : r,
      ),
    });
  }

  function addRow(): void {
    onChange({
      ...value,
      periodOverrides: [...value.periodOverrides, makeEmptyRow(basics)],
    });
  }

  // Delete behavior: hydrated rows (id present) soft-archive via status flip;
  // new rows (id absent) splice out entirely. savePricing in plan-editor.tsx
  // routes the archive branch to useDeleteInventoryPlanPeriodById.
  function deleteRow(rowIdx: number): void {
    const row = value.periodOverrides[rowIdx];
    if (!row) return;
    if (row.id == null) {
      onChange({
        ...value,
        periodOverrides: value.periodOverrides.filter((_, i) => i !== rowIdx),
      });
    } else {
      patchRow(rowIdx, { status: "Archived" });
    }
  }

  function reactivateRow(rowIdx: number): void {
    patchRow(rowIdx, { status: "Active" });
  }

  function moveRow(rowIdx: number, direction: -1 | 1): void {
    const target = rowIdx + direction;
    if (target < 0 || target >= value.periodOverrides.length) return;
    const next = [...value.periodOverrides];
    const tmp = next[rowIdx]!;
    next[rowIdx] = next[target]!;
    next[target] = tmp;
    onChange({ ...value, periodOverrides: next });
  }

  // Split rows into Active vs Archived; preserve original index.
  type IndexedRow = { row: PeriodOverride; idx: number };
  const activeRows: IndexedRow[] = [];
  const archivedRows: IndexedRow[] = [];
  value.periodOverrides.forEach((row, idx) => {
    if (row.status === "Archived") archivedRows.push({ row, idx });
    else activeRows.push({ row, idx });
  });

  const baselineCycleLabel = isRecurring
    ? "Baseline (1 month)"
    : `Baseline (${basics.validityDays || "—"} days)`;

  const baselineDisplayedPrice = monthlyPriceNum > 0
    ? formatRupee(monthlyPriceNum)
    : "—";

  const isSectionValid = computeIsSectionValid(value);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5 p-1" data-testid="plan-editor-pricing-section">
    <fieldset disabled={disabled} className="space-y-5 disabled:opacity-70">
        {/* Tax Rate */}
        <div className="space-y-1.5">
          <Label>Tax Rate % (blank = inherit space default 18%)</Label>
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={value.taxRatePct}
            onChange={(e) => patch({ taxRatePct: e.target.value })}
            placeholder="e.g. 18"
            className="max-w-[200px]"
          />
        </div>

        {/* Plan-level Security Deposit moved INTO the Baseline row of the
            Period Pricing table below — see baseline row's Deposit + Amount
            cells. Field state (securityDepositRequired + securityDepositAmount)
            is unchanged; only the surface moved for consistency with operator
            rows. */}

        {/* Period Pricing matrix */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Period Pricing</Label>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">
                Show Archived
              </Label>
              <Switch
                checked={value.showArchived ?? false}
                onCheckedChange={(v) => patch({ showArchived: v })}
                data-testid="plan-editor-pricing-show-archived"
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Baseline row reflects Basics. Add operator rows below for alternate
            (seats × period) tiers — Baseline + Discounted prices are computed
            live from monthly price × seats × cycle value.
          </p>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[170px]">Cycle</TableHead>
                  <TableHead className="w-[80px]">Seats</TableHead>
                  <TableHead className="w-[120px] text-right">
                    <span className="inline-flex items-center gap-1">
                      Baseline (₹)
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help">
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-[10px]">
                          monthly price × seats × cycle value
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </TableHead>
                  <TableHead className="w-[100px]">Discount %</TableHead>
                  <TableHead className="w-[130px] text-right">
                    Discounted (₹)
                  </TableHead>
                  <TableHead className="w-[80px]">Deposit</TableHead>
                  <TableHead className="w-[110px]">Amount (₹)</TableHead>
                  <TableHead className="w-[100px]">Validity (days)</TableHead>
                  <TableHead className="w-[120px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Row 1: Baseline (pinned, read-only) — seats=1, discount=0, Baseline === Discounted */}
                <TableRow
                  className="bg-muted/30"
                  data-testid="plan-editor-pricing-baseline-row"
                >
                  <TableCell className="font-medium text-sm">
                    {baselineCycleLabel}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs">1</span>
                  </TableCell>
                  {/* Baseline ₹ — bound directly to basics.monthlyPrice so the
                      operator can edit the plan-level price inline without
                      jumping to Basics. */}
                  <TableCell className="text-right">
                    {priceLocked ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-xs">{baselineDisplayedPrice}</span>
                        {onChangePrice && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-1.5 text-[11px]"
                            onClick={onChangePrice}
                            title="Change price (versioned, can only increase)"
                          >
                            Change
                          </Button>
                        )}
                      </div>
                    ) : onBasicsChange ? (
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={basics.monthlyPrice}
                        onChange={(e) =>
                          onBasicsChange({ monthlyPrice: e.target.value })
                        }
                        placeholder="—"
                        className="h-7 w-24 text-xs text-right"
                        data-testid="plan-editor-pricing-baseline-monthly-price"
                      />
                    ) : (
                      <span className="text-xs">{baselineDisplayedPrice}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs">0</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs">{baselineDisplayedPrice}</span>
                  </TableCell>
                  {/* Deposit toggle — wires the plan-level securityDepositRequired
                      flag inline in the baseline row, consistent with operator
                      rows. */}
                  <TableCell>
                    <Switch
                      checked={value.securityDepositRequired}
                      onCheckedChange={(v) =>
                        onChange({
                          ...value,
                          securityDepositRequired: v,
                          securityDepositAmount: v ? value.securityDepositAmount : "",
                        })
                      }
                      data-testid="plan-editor-pricing-baseline-deposit-switch"
                    />
                  </TableCell>
                  <TableCell>
                    {value.securityDepositRequired ? (
                      <Input
                        type="number"
                        min={0}
                        value={value.securityDepositAmount}
                        onChange={(e) =>
                          onChange({ ...value, securityDepositAmount: e.target.value })
                        }
                        placeholder="—"
                        className="h-7 w-24 text-xs"
                        data-testid="plan-editor-pricing-baseline-deposit-amount"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {/* Validity (days) — editable inline for ALL cadences. Bound
                      to basics.validityDays. computeDirtySections detects the
                      delta and marks Basics dirty. */}
                  <TableCell>
                    {onBasicsChange ? (
                      <Input
                        type="number"
                        min={1}
                        value={basics.validityDays}
                        onChange={(e) =>
                          onBasicsChange({ validityDays: e.target.value })
                        }
                        placeholder="—"
                        className="h-7 w-20 text-xs"
                        data-testid="plan-editor-pricing-baseline-validity"
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {basics.validityDays || "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={onFocusBasics}
                      disabled={!onFocusBasics}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground hover:underline disabled:cursor-default disabled:hover:no-underline"
                      data-testid="plan-editor-pricing-focus-basics"
                    >
                      <ChevronUp className="h-3 w-3" />
                      (from Basics)
                    </button>
                  </TableCell>
                </TableRow>

                {/* Rows 2..N: Operator-added Active rows */}
                {activeRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center text-xs text-muted-foreground py-6"
                    >
                      No operator-added rows yet. Click “+ Add Row” to define
                      alternate seats × period tiers.
                    </TableCell>
                  </TableRow>
                )}
                {activeRows.map(({ row, idx }, listIdx) => {
                  const isLegacy = row.isLegacyAbsolute === true;
                  const periodVal = row.periodValue === "" ? 0 : Number(row.periodValue);
                  const seatsVal = Math.max(1, Number(row.seats) || 1);
                  const baselinePrice = monthlyPriceNum * seatsVal * periodVal;
                  const discountNum = Number(row.discountPct) || 0;
                  const discountedPrice = baselinePrice * (1 - discountNum / 100);

                  // Legacy absolute rows render most cells as read-only '—'
                  // with the Discounted column carrying the legacy price + badge.
                  if (isLegacy) {
                    const legacyPriceNum = Number(row.price) || 0;
                    return (
                      <TableRow
                        key={`active-${idx}`}
                        data-testid="plan-editor-pricing-operator-row"
                        className="bg-amber-50/40"
                      >
                        <TableCell>
                          <span className="text-xs">
                            {row.periodValue || "?"} {row.periodUnit}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">—</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-xs text-muted-foreground">—</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">—</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1.5 justify-end">
                            <span className="text-xs">
                              {legacyPriceNum > 0
                                ? formatRupee(legacyPriceNum)
                                : "—"}
                            </span>
                            <Badge
                              variant="secondary"
                              className="text-[9px] font-normal"
                              data-testid="plan-editor-pricing-legacy-badge"
                            >
                              Legacy absolute
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">—</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">—</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs">
                            {row.validityDays || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => deleteRow(idx)}
                              aria-label="Archive legacy row"
                              data-testid="plan-editor-pricing-delete-row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow
                      key={`active-${idx}`}
                      data-testid="plan-editor-pricing-operator-row"
                    >
                      {/* Cycle: value + unit dropdown */}
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            value={row.periodValue === "" ? "" : row.periodValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              patchRow(idx, {
                                periodValue: v === "" ? "" : Number(v),
                              });
                            }}
                            placeholder="N"
                            className="h-8 w-16 text-xs"
                          />
                          <Select
                            value={row.periodUnit}
                            onValueChange={(v) =>
                              patchRow(idx, {
                                periodUnit: v as PeriodOverride["periodUnit"],
                              })
                            }
                          >
                            <SelectTrigger className="h-8 w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Hour">Hour</SelectItem>
                              <SelectItem value="Day">Day</SelectItem>
                              <SelectItem value="Month">Month</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>

                      {/* Seats input */}
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          value={row.seats}
                          onChange={(e) => {
                            const n = Math.max(
                              1,
                              parseInt(e.target.value || "1", 10) || 1,
                            );
                            patchRow(idx, { seats: n });
                          }}
                          className="h-7 w-16 text-xs"
                          data-testid="plan-editor-pricing-seats-input"
                        />
                      </TableCell>

                      {/* Baseline (read-only computed) */}
                      <TableCell className="text-right">
                        <span
                          className="text-xs tabular-nums"
                          data-testid="plan-editor-pricing-baseline-cell"
                        >
                          {baselinePrice > 0 ? formatRupee(baselinePrice) : "—"}
                        </span>
                      </TableCell>

                      {/* Discount % input */}
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="0.01"
                          value={row.discountPct}
                          onChange={(e) =>
                            patchRow(idx, { discountPct: e.target.value })
                          }
                          placeholder="0"
                          className="h-7 w-20 text-xs"
                        />
                      </TableCell>

                      {/* Discounted (read-only computed) */}
                      <TableCell className="text-right">
                        <span
                          className="text-xs tabular-nums"
                          data-testid="plan-editor-pricing-discounted-cell"
                        >
                          {baselinePrice > 0
                            ? formatRupee(discountedPrice)
                            : "—"}
                        </span>
                      </TableCell>

                      {/* Deposit Switch */}
                      <TableCell>
                        <Switch
                          checked={row.securityDepositRequired}
                          onCheckedChange={(v) =>
                            patchRow(idx, {
                              securityDepositRequired: v,
                              securityDepositAmount: v
                                ? row.securityDepositAmount
                                : "",
                            })
                          }
                        />
                      </TableCell>

                      {/* Deposit Amount */}
                      <TableCell>
                        {row.securityDepositRequired ? (
                          <Input
                            type="number"
                            min={0}
                            value={row.securityDepositAmount}
                            onChange={(e) =>
                              patchRow(idx, {
                                securityDepositAmount: e.target.value,
                              })
                            }
                            placeholder="—"
                            className="h-7 w-24 text-xs"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      {/* Validity (days) */}
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          value={row.validityDays}
                          onChange={(e) =>
                            patchRow(idx, { validityDays: e.target.value })
                          }
                          placeholder="—"
                          className="h-7 w-20 text-xs"
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => moveRow(idx, -1)}
                            disabled={listIdx === 0}
                            aria-label="Move row up"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => moveRow(idx, 1)}
                            disabled={listIdx === activeRows.length - 1}
                            aria-label="Move row down"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => deleteRow(idx)}
                            aria-label="Delete row"
                            data-testid="plan-editor-pricing-delete-row"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* + Add Row */}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="plan-editor-pricing-add-row"
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Row
            </Button>
          </div>

          {/* Archived rows section */}
          {value.showArchived && archivedRows.length > 0 && (
            <div
              className="space-y-2 pt-2"
              data-testid="plan-editor-pricing-archived-section"
            >
              <Label className="text-xs font-medium text-muted-foreground">
                Archived rows
              </Label>
              <div className="rounded-md border border-dashed">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[170px]">Cycle</TableHead>
                      <TableHead>Seats</TableHead>
                      <TableHead>Discount %</TableHead>
                      <TableHead>Validity (days)</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedRows.map(({ row, idx }) => (
                      <TableRow
                        key={`archived-${idx}`}
                        className="text-muted-foreground"
                      >
                        <TableCell className="text-xs">
                          {row.periodValue || "?"} {row.periodUnit}{" "}
                          <Badge variant="outline" className="ml-1 text-[10px]">
                            Archived
                          </Badge>
                          {row.isLegacyAbsolute && (
                            <Badge
                              variant="secondary"
                              className="ml-1 text-[9px] font-normal"
                            >
                              Legacy absolute
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.isLegacyAbsolute ? "—" : row.seats}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.isLegacyAbsolute
                            ? "—"
                            : row.discountPct || "0"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.validityDays || "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {!row.isLegacyAbsolute && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px]"
                              onClick={() => reactivateRow(idx)}
                              data-testid="plan-editor-pricing-reactivate-row"
                            >
                              Reactivate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {/* Save bar */}
        </fieldset>
      <div className="flex items-center justify-end gap-3 pt-2">
          {disabled ? (
            <Button
              type="button"
              variant="outline"
              onClick={onEdit}
              data-testid="plan-editor-pricing-edit"
            >
              Edit
            </Button>
          ) : (
            <>
              {!isSectionValid && (
                <p className="text-xs text-muted-foreground">
                  Fix the highlighted rows to continue (each row needs a positive
                  cycle value and seats ≥ 1; deposit toggle needs an amount).
                </p>
              )}
              {isSectionValid && (
                <Button
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  data-testid="plan-editor-pricing-save"
                >
                  {saving ? "Saving…" : "Save & Continue"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
